import { useEffect, useMemo, useState } from "react";
import { ArrowDownRight, ArrowUpRight, Minus, Sigma } from "lucide-react";
import { format, subDays, subMonths, startOfMonth } from "date-fns";

import { Skeleton } from "@/components/ui/skeleton";
import { GoogleIcon } from "@/components/icons/google";
import { MetaIcon } from "@/components/icons/meta";

import { cn } from "@/lib/utils";
import type {
  OverviewV2Direction,
  OverviewV2Kpi,
  OverviewV2KpiBucket,
  OverviewV2KpiKey,
  OverviewV2SnapshotPlatform,
  OverviewV2SnapshotPlatformValue,
} from "@/schema/brand-os/paid-media-overview-v2";

import { Sparkline } from "./sparkline";
import { deltaTone, formatDelta, formatKpiValue, platformLabel } from "./format";

interface KpiSnapshotProps {
  /**
   * The full `platforms[]` array from the snapshot response — source of
   * truth for both the tab list and the visible KPI block. Iterate as-is;
   * the backend may include a synthetic `"combined"` entry when ≥2 real
   * platforms returned data.
   */
  platforms: OverviewV2SnapshotPlatform[];
  currency: string;
  /** From the snapshot envelope — used to compute period sub-headers. */
  syncedAt: string;
}

/**
 * Performance snapshot table.
 *
 * Layout: 4 columns — KPI | Yesterday | MTD | Prev. month. Each value cell
 * is tinted per its direction (positive → green tint, negative → red tint,
 * stable → neutral). Sparklines render inline inside the Yesterday cell —
 * the 7-day series ends on yesterday and is the only trend visualisation
 * we surface here. We deliberately do not render a sparkline for MTD /
 * Prev. month because the backend doesn't ship a daily series for them.
 *
 * Tabs are derived from `snapshot.platforms[]` directly (NOT from
 * `platforms_included`) so the synthetic `"combined"` entry shows up
 * automatically when the backend ships it. Default to "combined" when
 * present, else the first real platform — most users want the rolled-up
 * view by default and can drill down per source.
 */
export function KpiSnapshot({ platforms, currency, syncedAt }: KpiSnapshotProps) {
  // Reorder so the synthetic `"combined"` tab (rendered as "All") leads,
  // matching the previous "All / Google / Meta" muscle memory. Backend
  // ships [google, meta, combined] but we want [combined, google, meta]
  // visually. We reorder a copy — never mutate the response.
  const tabValues = useMemo<OverviewV2SnapshotPlatformValue[]>(() => {
    const all = platforms.map((p) => p.platform);
    const combined = all.filter((v) => v === "combined");
    const rest = all.filter((v) => v !== "combined");
    return [...combined, ...rest];
  }, [platforms]);

  const defaultTab = useMemo<OverviewV2SnapshotPlatformValue | null>(() => {
    if (tabValues.includes("combined")) return "combined";
    return tabValues[0] ?? null;
  }, [tabValues]);

  const [tab, setTab] = useState<OverviewV2SnapshotPlatformValue | null>(defaultTab);

  // If the response shape changes between fetches (e.g. a refresh that
  // gains/loses the "combined" tab) and the active tab disappears, snap
  // back to the new default. Keeps the UI from getting stuck on a tab
  // whose data no longer exists.
  useEffect(() => {
    if (!tab || !tabValues.includes(tab)) {
      setTab(defaultTab);
    }
  }, [tab, tabValues, defaultTab]);

  const visiblePlatform = useMemo(
    () => platforms.find((p) => p.platform === tab) ?? platforms[0] ?? null,
    [platforms, tab],
  );

  // Backend order varies by platform; we normalize to Spend → Conversions →
  // CPA → ROAS for every tab (Google, Meta, combined). Unknown keys follow
  // in backend order.
  const orderedKpis = useMemo(
    () => (visiblePlatform ? sortKpis(visiblePlatform.kpis) : []),
    [visiblePlatform],
  );

  const periodLabels = usePeriodLabels(syncedAt);

  if (!platforms.length || !visiblePlatform) {
    return <p className="text-sm text-muted-foreground">No KPI data returned for any connected platform.</p>;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-end">
        <PlatformTabs value={tab ?? defaultTab} onChange={setTab} platforms={tabValues} />
      </div>

      <div className="overflow-x-auto">
        <div className="min-w-[640px] space-y-5">
          <KpiTableHeader periodLabels={periodLabels} />
          <div className="space-y-1">
            {orderedKpis.map((kpi) => (
              <KpiRow key={kpi.key} kpi={kpi} currency={currency} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// --- table chrome --------------------------------------------------------

const COLS = "grid grid-cols-[140px_1fr_1fr_1fr] gap-x-3";

function KpiTableHeader({ periodLabels }: { periodLabels: PeriodLabels }) {
  return (
    <div className={cn(COLS, "text-xs uppercase tracking-wide text-muted-foreground")}>
      <div className="font-medium">KPI</div>
      <ColHeader title="Yesterday" subtitle={periodLabels.yesterday} />
      <ColHeader title="MTD" subtitle={periodLabels.mtd} />
      <ColHeader title="Prev. month" subtitle={periodLabels.prevMonth} />
    </div>
  );
}

function ColHeader({ title, subtitle }: { title: string; subtitle: string }) {
  return (
    <div className="flex flex-col">
      <span className="font-medium">{title}</span>
      <span className="text-[10px] normal-case tracking-normal text-muted-foreground/70">{subtitle}</span>
    </div>
  );
}

// --- one KPI row --------------------------------------------------------

function KpiRow({ kpi, currency }: { kpi: OverviewV2Kpi; currency: string }) {
  const sparkTone = useSparklineTone(kpi);
  return (
    <div className={cn(COLS, "items-center py-2.5")}>
      <KpiLabel kpi={kpi} currency={currency} />
      <ValueCell
        value={kpi.yesterday.value}
        bucket={kpi.yesterday}
        kpiKey={kpi.key}
        currency={currency}
        sparkline={kpi.sparkline}
        sparkTone={sparkTone}
      />
      <ValueCell
        value={kpi.mtd.value}
        bucket={kpi.mtd}
        kpiKey={kpi.key}
        currency={currency}
      />
      <ValueCell
        value={kpi.prev_month.value}
        bucket={null}
        kpiKey={kpi.key}
        currency={currency}
      />
    </div>
  );
}

function KpiLabel({ kpi, currency }: { kpi: OverviewV2Kpi; currency: string }) {
  // Reference number under the label = previous month's value. Mirrors the
  // mock's "✓ 3.5x" / "- ₹450" benchmark hint.
  const ref = kpi.prev_month.value;
  return (
    <div className="flex flex-col">
      <span className="text-sm font-medium text-foreground">{kpi.label}</span>
      {ref != null && (
        <span className="text-[10px] text-muted-foreground/70">
          ref · {formatKpiValue(kpi.key, ref, currency)}
        </span>
      )}
    </div>
  );
}

function ValueCell({
  value,
  bucket,
  kpiKey,
  currency,
  sparkline,
  sparkTone,
}: {
  value: number | null;
  /** Null means "render plain — no tint, no delta". Used for Prev. month. */
  bucket: OverviewV2KpiBucket | null;
  kpiKey: OverviewV2KpiKey;
  currency: string;
  sparkline?: (number | null)[];
  sparkTone?: "positive" | "negative" | "neutral";
}) {
  const tone = bucket ? deltaTone(bucket.delta_pct, bucket.direction, kpiKey) : "neutral";
  return (
    <div
      className={cn(
        "rounded-md px-2.5 py-1.5 transition-colors",
        bucket && tone === "positive" && "bg-success/8",
        bucket && tone === "negative" && "bg-destructive/8",
      )}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="text-xl font-semibold tracking-tight tabular-nums">
          {formatKpiValue(kpiKey, value, currency)}
        </span>
        {sparkline && sparkline.length > 0 && (
          <Sparkline values={sparkline} tone={sparkTone ?? "neutral"} className="h-6 w-16" />
        )}
      </div>
      {bucket && bucket.delta_pct != null && <DeltaLine bucket={bucket} kpiKey={kpiKey} tone={tone} />}
    </div>
  );
}

function DeltaLine({
  bucket,
  kpiKey: _kpiKey,
  tone,
}: {
  bucket: OverviewV2KpiBucket;
  kpiKey: OverviewV2KpiKey;
  tone: "positive" | "negative" | "neutral";
}) {
  const Icon = directionIcon(bucket.direction);
  const colour = tone === "positive" ? "text-success" : tone === "negative" ? "text-destructive" : "text-muted-foreground";
  return (
    <span className={cn("inline-flex items-center gap-0.5 text-[11px] font-medium tabular-nums", colour)}>
      <Icon className="size-3" strokeWidth={2.5} />
      {formatDelta(bucket.delta_pct)}
    </span>
  );
}

function directionIcon(direction: OverviewV2Direction) {
  if (direction === "up") return ArrowUpRight;
  if (direction === "down") return ArrowDownRight;
  return Minus;
}

// --- platform tabs (segmented control) -----------------------------------

function PlatformTabs({
  value,
  onChange,
  platforms,
}: {
  value: OverviewV2SnapshotPlatformValue | null;
  onChange: (v: OverviewV2SnapshotPlatformValue) => void;
  platforms: OverviewV2SnapshotPlatformValue[];
}) {
  // No tabs when there's only one platform group — toggling between a
  // single tab and itself would be UI noise. (The single platform may be
  // `"combined"` in degenerate cases — we still hide tabs and just render
  // its KPIs directly.)
  if (platforms.length <= 1) return null;

  return (
    <div className="inline-flex items-center rounded-lg border bg-muted/30 p-0.5 text-xs">
      {platforms.map((p) => (
        <TabButton key={p} active={value === p} onClick={() => onChange(p)}>
          <PlatformGlyph platform={p} />
          {platformLabel(p)}
        </TabButton>
      ))}
    </div>
  );
}

/**
 * Tab glyph: brand mark for known real platforms, Σ for the synthetic
 * `"combined"` group (rendered as "All"). The Σ is the user's hint that
 * this view is derived (not a separate data source) — paired with the
 * "All" label it reads as "Σ All". Unknown platforms (forward-compat for
 * future ad networks) fall through to no glyph rather than crashing.
 */
function PlatformGlyph({ platform }: { platform: OverviewV2SnapshotPlatformValue }) {
  if (platform === "google") return <GoogleIcon className="size-3" />;
  if (platform === "meta") return <MetaIcon className="size-3" />;
  if (platform === "combined") return <Sigma className="size-3" strokeWidth={2.5} />;
  return null;
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "inline-flex items-center gap-1 rounded-md px-2.5 py-1 font-medium transition-colors",
        active ? "bg-background text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground",
      )}
    >
      {children}
    </button>
  );
}

// --- helpers ------------------------------------------------------------

/**
 * Canonical row order for the snapshot table (all platform tabs). Scale
 * first, then efficiency. Extra keys from the backend keep their order
 * after these.
 */
const KPI_ORDER: OverviewV2KpiKey[] = ["spend", "conversions", "cpa", "roas"];

function sortKpis(kpis: OverviewV2Kpi[]): OverviewV2Kpi[] {
  const indexOf = (k: OverviewV2KpiKey) => {
    const i = KPI_ORDER.indexOf(k);
    return i === -1 ? KPI_ORDER.length : i;
  };
  return [...kpis].sort((a, b) => indexOf(a.key) - indexOf(b.key));
}

interface PeriodLabels {
  yesterday: string;
  mtd: string;
  prevMonth: string;
}

function usePeriodLabels(syncedAt: string): PeriodLabels {
  return useMemo(() => {
    const now = new Date(syncedAt);
    if (Number.isNaN(now.getTime())) {
      return { yesterday: "", mtd: "", prevMonth: "" };
    }
    const yesterday = subDays(now, 1);
    const mtdStart = startOfMonth(yesterday);
    const prev = subMonths(yesterday, 1);
    return {
      yesterday: format(yesterday, "MMM d"),
      mtd: `${format(mtdStart, "MMM d")}–${format(yesterday, "d")}`,
      prevMonth: format(prev, "MMMM"),
    };
  }, [syncedAt]);
}

/**
 * Sparkline tone follows the literal sign of the trend: an upward last-vs-
 * first move is green, downward is red, flat is neutral. Matches the
 * sign-driven convention used everywhere else in the page (no metric-
 * polarity flip — the user reads the colour as "value moved in this
 * direction", not "this is good/bad for you").
 */
function useSparklineTone(kpi: OverviewV2Kpi): "positive" | "negative" | "neutral" {
  const defined = kpi.sparkline.filter((v): v is number => v != null);
  if (defined.length < 2) return "neutral";
  const first = defined[0];
  const last = defined[defined.length - 1];
  return deltaTone(last - first, undefined, kpi.key);
}

export function KpiSnapshotSkeleton() {
  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Skeleton className="h-7 w-40 rounded-lg" />
      </div>
      <div className={cn(COLS, "text-xs uppercase tracking-wide text-muted-foreground")}>
        <Skeleton className="h-3 w-10" />
        <Skeleton className="h-3 w-16" />
        <Skeleton className="h-3 w-12" />
        <Skeleton className="h-3 w-20" />
      </div>
      {Array.from({ length: 4 }, (_, i) => (
        <div key={i} className={cn(COLS, "items-center py-2.5")}>
          <div className="space-y-1">
            <Skeleton className="h-4 w-20" />
            <Skeleton className="h-3 w-12" />
          </div>
          <Skeleton className="h-12 w-full rounded-md" />
          <Skeleton className="h-12 w-full rounded-md" />
          <Skeleton className="h-12 w-full rounded-md" />
        </div>
      ))}
    </div>
  );
}
